import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { FORTA_BLUE, FORTA_GRAY4, FORTA_GRAY3, FORTA_WHITE } from '../../utils/colors';
import { cleanNumericInputValue } from '../../helpers';
import { getThemeColor } from '../../utils/common';
import { Themes } from '../../constants';
import FHButton from './FHButton';
import PlusIcon from '../svgs/PlusIcon';

import type { ChangeEvent } from 'react';

interface DurationInputProps {
  durationType: string; // Duration type can be 'seconds' or 'minutes'
  colorTheme?: Themes;
  reset?: boolean;
  onAdd: (durationToAdd: number) => void;
  isDisabled: boolean;
  dataTestId?: string;
}

const TimeInputContainer = styled(Box)<{ colorTheme?: Themes }>(({ colorTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  width: '172px',
  height: 40,
  minHeight: 40,
  backgroundColor: getThemeColor(colorTheme).light3,
  borderRadius: '8px',
  padding: '8px',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  gap: '8px',
}));

const StyledTextField = styled(TextField)<{ colorTheme?: Themes }>(({ colorTheme }) => ({
  width: '40px',
  '& .MuiInputBase-root': {
    height: '24px',
    backgroundColor: getThemeColor(colorTheme).light3,
    color: FORTA_GRAY4,
  },
  '& .MuiInputBase-input': {
    padding: '8px',
    textAlign: 'center',
  },
  '& .MuiInputBase-input:focus': {
    border: 'none',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
      borderBottom: `0.5px solid ${FORTA_GRAY3}`,
      borderRadius: '0px',
    },
    '&:hover fieldset': {
      border: 'none',
      borderBottom: `0.5px solid ${FORTA_GRAY3}`,
      borderRadius: '0px',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
      borderBottom: `0.5px solid ${FORTA_GRAY3}`,
      borderRadius: '0px',
    },
  },
}));

const FHDurationInput: React.FC<DurationInputProps> = (props: DurationInputProps) => {
  const { durationType, colorTheme = Themes.Primary, reset, isDisabled, onAdd, dataTestId } = props;
  const [newDuration, setNewDuration] = useState<any>(null);

  useEffect(() => {
    if (reset) {
      setNewDuration('');
    }
  }, [reset]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'left',
        gap: '16px',
      }}
    >
      <TimeInputContainer colorTheme={colorTheme}>
        <StyledTextField
          data-testid={`fill${dataTestId}`}
          placeholder="00"
          colorTheme={colorTheme}
          disabled={isDisabled}
          value={cleanNumericInputValue((newDuration ?? '').toString())}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const val = cleanNumericInputValue(e.target.value);
            const numVal = val ? Number(val) : '';
            setNewDuration(numVal);
          }}
        />
        <Typography sx={{ color: FORTA_GRAY4 }}>{durationType}</Typography>
      </TimeInputContainer>
      <FHButton
        data-testid={`add${dataTestId}`}
        size="small"
        startIcon={<PlusIcon color={FORTA_GRAY4} />}
        disabled={isDisabled}
        sx={{
          width: 'fit-content',
          backgroundColor: FORTA_WHITE,
          color: FORTA_GRAY4,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: FORTA_BLUE.light2,
          ':hover': { color: FORTA_GRAY4 },
          ':disabled': {
            backgroundColor: FORTA_WHITE,
            color: FORTA_GRAY4,
            opacity: 0.5,
          },
        }}
        lightBackground={true}
        onClick={() => {
          if (newDuration) {
            onAdd(newDuration);
          } else {
            setNewDuration('');
            toast.error('Duration cannot be zero. If no instances occur, do not add duration.');
          }
        }}
      >
        Add
      </FHButton>
    </Box>
  );
};

export default FHDurationInput;
